import {
    GENERAL_MESSAGE,
    GeneralBalancesPayload,
    GeneralEvent,
    GeneralPositionsPayload,
    GeneralPullPayload,
    InvalidKeyPayload,
    MemberUpdatePayload,
    MessageHandler,
} from './websocketTypes';
import { batch } from 'react-redux';
import throttle from 'lodash/throttle';
import store, { RootState } from '../../store/store';
import {
    removeBalancesByAccountId,
    setHyperLiquidBalances,
    setInitialBalances,
    setTotalValues,
} from '@/state/balances/balancesSlice';
import { selectHyperLiquidBalancesById } from '@/state/balances/balancesSelector';
import {
    removeDataLoadingKey,
    setApiKeyLoading,
    setGeneralLoadingStatus,
    setGlobalUnlockData,
    toggleNotificationToast,
} from '@/state/general/generalSlice';
import { DATA_LOADING_KEYS, LOADING_STATE } from '@/state/general/generalTypes';
import {
    type ExchangeAccount,
    Pull,
    PULL_GENERAL_STATUS,
} from '@/state/exchanges/exchangesSliceTypes';
import { accountsQuery } from '@/api/queries/accountsQuery';
import { selectAccountById } from '@/api/selectors/accountSelectors';
import { bannersQuery } from '@/api/queries/bannersQuery';
import { removeBannerByApiKey, updatePullBanner } from '@/state/banners/bannersSlice';
import { resetPullStatus, setOverallPnl, setPullStatus } from '@/state/exchanges/exchangesSlice';
import { getTransformedPositions, isDateAvailable } from '../helpers/helpers';
import { Leg } from '@/state/legs/legsTypes';
import { endOfDay, startOfDay } from 'date-fns';
import { removeLegsByAccountId, setLegsById, updateLeg } from '@/state/legs/legsSlice';
import { getMemberProfile } from '@/api/settingsApi';
import { getActualSubscription } from '@/api/paymentsApi';
import {
    updateMemberProfile,
    updateMemberProfileWS,
    updateProfileParts,
    updateUsedSlots,
} from '@/state/member/memberSlice';
import { setSubscription } from '@/state/payments/paymentsSlice';
import {
    deleteVerificationPageByApiKeyId,
    updatePageById,
} from '@/state/verification/verificationSlice';
import { COMBINED_REPORT_QUERY_KEY, REPORT_QUERY_KEY } from '@/api/queries/reportQuery';
import {
    getLegsById,
    getLegsForMultipleAccounts,
    getSymbolsForMultipleAccounts,
    getFuturesPnlApi,
    getFuturesPnlHyperliquid,
} from '@/api/exchangesApi';
import { getAccountById } from '@/api/apiManagerApi';
import { setCombinedLegs, updateCombinedLegsList } from '@/state/shortStorage/shortStorageSlice';
import { ROUTES } from '@/lib/constants';
import { definedFetchLegsParams } from '@/hooks/useGetFetchLegsParams';
import { Banner } from '@/state/banners/bannersTypes';
import { UsedSlots } from '@/state/member/memberTypes';
import { EXCHANGES } from '@/lib/types/generalTypes';
import { Notification } from '@/state/notifications/notificationsTypes';
import { addNotificationFromWs } from '@/state/notifications/notificationsSlice';
import { updateLeaderboardSettings } from '@/state/leaderboard/leaderboardSlice';
import { getBalanceByAccountId } from '@/api/balancesPositionsApi';
import syncStoreService from '@/store/syncStore/SyncStoreService';
import { getGlobalUnlockData } from '@/api/generalApi';
import { updateJournalSymbolFilter } from '@/state/journals/journalsSlice';
import { getSpotTrades } from '@/api/balancesPositionsApi';
import { queryClient } from '@/lib/query/queryClient';
import { futurePnlQueryKey } from '@/api/queries/futurePnlQuery';

const PULL_STATUS_THROTTLE_MS = 500;

class GeneralEventsHandler implements MessageHandler {
    constructor() {}

    private getState = (): RootState => store.getState();

    private throttledPullDispatchers = new Map<number, ReturnType<typeof throttle>>();

    private getThrottledPullDispatch = (apiKeyId: number) => {
        if (!this.throttledPullDispatchers.has(apiKeyId)) {
            this.throttledPullDispatchers.set(
                apiKeyId,
                throttle((payload: Pull) => {
                    store.dispatch(setPullStatus(payload));
                }, PULL_STATUS_THROTTLE_MS),
            );
        }
        return this.throttledPullDispatchers.get(apiKeyId) as (payload: Pull) => void;
    };

    handleMessage = (ws: WebSocket | null, message: GeneralEvent<any>): void => {
        if (!ws) return;
        switch (message.payload.type) {
            case GENERAL_MESSAGE.BALANCES:
                this.handleBalances(message);
                break;
            case GENERAL_MESSAGE.OPEN_POSITIONS:
                this.handleOpenPositions(message);
                break;
            case GENERAL_MESSAGE.DATA_MESSAGE:
            case GENERAL_MESSAGE.DATA_PULL:
                this.handlePullData(message);
                break;
            case GENERAL_MESSAGE.CP_UPDATE:
            case GENERAL_MESSAGE.STRIPE_UPDATE:
                this.handlePaymentSubscriptionUpdate();
                break;
            case GENERAL_MESSAGE.INVALIDATE_API_KEY:
                this.handleInvalidateApiKey(message);
                break;
            case GENERAL_MESSAGE.MARK_API_KEY_AS_FAULTY:
                this.markApiKeyAsFaulty(message);
                break;
            case GENERAL_MESSAGE.MEMBER_ACCOUNT_TYPE:
                this.handleAccountTypeChange(message);
                break;
            case GENERAL_MESSAGE.LEG_CHANGED:
                this.handleLegChange(message);
                break;
            case GENERAL_MESSAGE.NEW_BANNER:
                this.handleNewBanner(message);
                break;
            case GENERAL_MESSAGE.DISMISS_BANNER:
                this.handleDismissBanner(message);
                break;
            case GENERAL_MESSAGE.MEMBER_UPDATE:
                this.handleMemberUpdate(message);
                break;
            case GENERAL_MESSAGE.SLOTS_UPDATE:
                this.handleUsedSlotsUpdate(message);
                break;
            case GENERAL_MESSAGE.VERIFICATION_PAGE_DEPLOY:
                this.handleVerificationPagesDeploy(message);
                break;
            case GENERAL_MESSAGE.VERIFICATION_PAGE_UPDATE:
                this.handleVerificationPageUpdate(message);
                break;
            case GENERAL_MESSAGE.NOTIFICATION_CREATED:
                this.handleNotifications(message);
                break;
            case GENERAL_MESSAGE.LEADERBOARD_UPDATE:
                this.handleLeaderboardUpdate(message);
                break;
            case GENERAL_MESSAGE.MEMBER_SETTINGS_UPDATE:
                this.handleMemberSettingsChanged(message);
                break;
            case GENERAL_MESSAGE.LEADERBOARD_SETTINGS_UPDATE:
                this.handleLeaderboardSettingsChanged(message);
                break;
            case GENERAL_MESSAGE.ACTIVITY_STREAM_UPDATE:
                this.handleActivityStreamUpdate(message);
                break;
        }
    };

    private handleNewBanner = (_message: GeneralEvent<{ type: string; data: Banner }>) => {
        queryClient.invalidateQueries({ queryKey: bannersQuery.queryKey });
    };
    private handleDismissBanner = (_message: GeneralEvent<{ type: string; data: Banner }>) => {
        queryClient.invalidateQueries({ queryKey: bannersQuery.queryKey });
    };

    private handleLegChange = (message: GeneralEvent<{ leg: Leg }>) => {
        const updatedLeg: Leg = message.payload.leg;
        if (location.pathname === ROUTES.HOME) store.dispatch(updateCombinedLegsList(updatedLeg));
        else store.dispatch(updateLeg(updatedLeg));
        queryClient.invalidateQueries({ queryKey: ['recentTrades'] });
        queryClient.invalidateQueries({ queryKey: ['accountRecentTrades'] });
        queryClient.invalidateQueries({ queryKey: ['legsSummaryAnalytics', 'home'] });
        queryClient.invalidateQueries({ queryKey: ['accountAnalytics'] });
    };
    private handleBalances = (message: GeneralEvent<GeneralBalancesPayload>): void => {
        const { totalDeposits = 0, totalWithdrawals = 0, apiKeyId } = message.payload;

        const wallets = Object.keys(message.payload.balances);
        if (wallets.length > 0) {
            // map accountIds and exchanges
            const state = store.getState();
            const accounts =
                queryClient.getQueryData<ExchangeAccount[]>(accountsQuery.queryKey) ?? [];
            const exchanges: any = {};
            accounts.forEach((account) => {
                exchanges[account.id] = account.exchange;
            });

            const result: any[] = [];
            wallets.forEach((wallet) => {
                const balances = message.payload.balances[wallet]?.balances || [];
                balances.forEach((balance) => {
                    result.push({
                        accountId: `${apiKeyId}`,
                        wallet,
                        value: balance.value,
                        symbol: balance.symbol,
                        exchange: exchanges[+apiKeyId],
                        digits: balance?.digits || 1000,
                        conversionId: `${balance?.symbol?.toLowerCase() || ''}-usd-${exchanges[+apiKeyId]?.toLowerCase() || ''}`,
                    });
                });
            });

            if (exchanges[+apiKeyId] === EXCHANGES.HYPERLIQUID) {
                selectHyperLiquidBalancesById(state, { accountId: +apiKeyId }).forEach(
                    (element) => {
                        if (!element?.balance && element?.coin) {
                            const splitCoinValue = element.coin.split(' ');
                            const coin = splitCoinValue?.[0] || element.coin;
                            result.push({
                                accountId: `${apiKeyId}`,
                                value: 0,
                                symbol: element.coin,
                                exchange: EXCHANGES.HYPERLIQUID,
                                digits: element?.digits || 1000,
                                conversionId: `${coin.toLowerCase()}-usd-hyperliquid`,
                            });
                        }
                    },
                );
            }
            syncStoreService.setBalanceRowsInTransaction(result);
        }

        store.dispatch(setInitialBalances(message.payload));
        store.dispatch(
            setApiKeyLoading({
                apiKey: apiKeyId,
                loadingKey: 'balances',
                status: LOADING_STATE.SUCCESS,
            }),
        );
        store.dispatch(
            setTotalValues({
                accountId: apiKeyId,
                values: { totalDeposits, totalWithdrawals },
            }),
        );
    };

    private handleOpenPositions = (message: GeneralEvent<GeneralPositionsPayload>): void => {
        const { positions, apiKeyId } = message.payload;
        const accounts = queryClient.getQueryData<ExchangeAccount[]>(accountsQuery.queryKey) ?? [];
        const account = selectAccountById(accounts, apiKeyId);
        if (account?.keyError) return;
        const transformed: any[] = getTransformedPositions(positions, apiKeyId);
        syncStoreService.setPositionsForAccount(transformed, `${apiKeyId}`);
    };

    private handlePullData = async (message: GeneralEvent<GeneralPullPayload>): Promise<void> => {
        const { apiKeyId, status, step, totalSteps } = message.payload;
        const currentPull: Pull | undefined = this.getState().exchanges.pullStatus?.[apiKeyId];
        const isPullVisible: boolean | undefined = this.getState().banners.pullBanners?.[apiKeyId];

        const isTerminalStatus =
            status === PULL_GENERAL_STATUS.COMPLETED || status === PULL_GENERAL_STATUS.STOPPED;

        if (isTerminalStatus) {
            this.throttledPullDispatchers.get(apiKeyId)?.cancel();
            batch(() => {
                store.dispatch(setPullStatus(message.payload));
                if (!currentPull || !isPullVisible) {
                    store.dispatch(updatePullBanner({ accountId: apiKeyId, visible: true }));
                }
            });
        } else {
            this.getThrottledPullDispatch(apiKeyId)(message.payload);
            if (!currentPull || !isPullVisible) {
                store.dispatch(updatePullBanner({ accountId: apiKeyId, visible: true }));
            }
        }

        if (status === PULL_GENERAL_STATUS.COMPLETED && step === totalSteps) {
            this.runLegsUpdate(apiKeyId)
                .then(() => {
                    queryClient.invalidateQueries({ queryKey: ['recentTrades'] });
                    queryClient.invalidateQueries({ queryKey: ['accountRecentTrades'] });
                    queryClient.invalidateQueries({ queryKey: ['legsSummaryAnalytics', 'home'] });
                    queryClient.invalidateQueries({ queryKey: ['accountAnalytics'] });
                    getGlobalUnlockData().then((response) => {
                        store.dispatch(setGlobalUnlockData(response.data || null));
                    });
                })
                .catch((error) => {
                    console.error('Error running legs update', error);
                });
            if (currentPull?.exchange === EXCHANGES.HYPERLIQUID) {
                const response = await getSpotTrades({
                    exchange: currentPull?.exchange,
                    accountId: currentPull.apiKeyId,
                    showDust: true,
                    showBalances: true,
                });

                const responsePnl = await getFuturesPnlHyperliquid(currentPull.apiKeyId);
                store.dispatch(
                    setOverallPnl({
                        accountId: currentPull.apiKeyId,
                        pnl: responsePnl.data.overallPnl,
                    }),
                );
                store.dispatch(
                    setHyperLiquidBalances({
                        accountId: currentPull.apiKeyId,
                        values: response.data,
                    }),
                );
            }
        }
    };

    private handlePaymentSubscriptionUpdate = async (): Promise<void> => {
        const [profile, subscription] = await Promise.all([
            getMemberProfile(),
            getActualSubscription(),
        ]);

        batch(() => {
            store.dispatch(updateMemberProfile(profile.data));
            store.dispatch(setSubscription(subscription?.data || null));
        });
    };

    private handleInvalidateApiKey = (message: GeneralEvent<InvalidKeyPayload>): void => {
        const { apiKeyId, exchange } = message.payload;
        batch(() => {
            store.dispatch(deleteVerificationPageByApiKeyId({ apiKeyId }));
            store.dispatch(removeBalancesByAccountId({ accountId: apiKeyId }));
            store.dispatch(removeLegsByAccountId({ accountId: apiKeyId }));
            store.dispatch(removeDataLoadingKey({ apiKeyId }));
            store.dispatch(removeBannerByApiKey({ apiKeyId }));
            store.dispatch(resetPullStatus(apiKeyId));
            queryClient.removeQueries({ queryKey: [...REPORT_QUERY_KEY, apiKeyId] });
            queryClient.removeQueries({ queryKey: COMBINED_REPORT_QUERY_KEY });
            queryClient.setQueryData<ExchangeAccount[]>(accountsQuery.queryKey, (cached) =>
                cached?.filter((acc) => acc.id !== apiKeyId),
            );
        });

        // data invalidation in tinybase store
        syncStoreService.invalidateBalancesForAccount(`${apiKeyId}`);

        // navigate home
        if (window.location.pathname.includes(`/${exchange}/${apiKeyId}`)) {
            window.location.href = '/';
        }
    };
    private markApiKeyAsFaulty = (message: any): void => {
        const { apiKeyId, reason } = message.payload;
        batch(() => {
            store.dispatch(removeBalancesByAccountId({ accountId: apiKeyId }));
            queryClient.setQueryData<ExchangeAccount[]>(accountsQuery.queryKey, (cachedAccounts) =>
                cachedAccounts?.map((cachedAccount) =>
                    cachedAccount.id === apiKeyId
                        ? { ...cachedAccount, keyError: reason }
                        : cachedAccount,
                ),
            );
            store.dispatch(
                setApiKeyLoading({
                    apiKey: apiKeyId,
                    loadingKey: 'balances',
                    status: LOADING_STATE.ERROR,
                }),
            );
            store.dispatch(
                setApiKeyLoading({
                    apiKey: apiKeyId,
                    loadingKey: 'positions',
                    status: LOADING_STATE.ERROR,
                }),
            );
        });
    };
    private handleAccountTypeChange = async (message: GeneralEvent<any>): Promise<void> => {
        const subscription = await getActualSubscription();

        batch(() => {
            store.dispatch(updateMemberProfileWS(message.payload.data));
            store.dispatch(setSubscription(subscription?.data || null));
        });
    };

    private runLegsUpdate = async (apiKeyId: number): Promise<void> => {
        try {
            const {
                legs: { dateSelection, symbolFilter, symbolsList },
                shortStorage: { selectedAccounts, legsFilters },
            } = this.getState();

            const exchangeAccounts =
                queryClient.getQueryData<ExchangeAccount[]>(accountsQuery.queryKey) ?? [];
            const updatedAccount = selectAccountById(exchangeAccounts, apiKeyId);
            const isInSelectedAccounts = selectedAccounts.find(
                (item) => +item.accountId === +apiKeyId,
            );
            const params = definedFetchLegsParams([apiKeyId], {
                dateSelection,
                symbolsList,
                symbolFilter,
                legsFilters,
                accounts: exchangeAccounts,
            });
            if (updatedAccount) {
                store.dispatch(
                    setApiKeyLoading({
                        apiKey: apiKeyId,
                        loadingKey: 'legs',
                        status: LOADING_STATE.LOADING,
                    }),
                );

                const [legs, account] = await Promise.all([
                    getLegsById({ params }),
                    getAccountById({ accountId: updatedAccount.id }),
                ]);

                if (legs?.data?.items) {
                    let symbolsParams: any = {
                        startDate: dateSelection.startDate,
                        endDate: dateSelection.endDate,
                    };

                    if (!isDateAvailable(dateSelection.label)) {
                        symbolsParams = {
                            startDate: startOfDay(new Date('01/01/2016')).toISOString(),
                            endDate: endOfDay(new Date()).toISOString(),
                        };
                    }
                    symbolsParams.accountIds = [updatedAccount.id];

                    const { data: symbols } = await getSymbolsForMultipleAccounts(symbolsParams);

                    batch(() => {
                        store.dispatch(
                            setLegsById({
                                legs: legs.data,
                                accountId: updatedAccount.id,
                            }),
                        );

                        store.dispatch(updateJournalSymbolFilter(symbols.map((s) => s.symbol)));
                    });

                    queryClient.setQueryData<ExchangeAccount[]>(
                        accountsQuery.queryKey,
                        (cachedAccounts) =>
                            cachedAccounts?.map((cachedAccount) =>
                                cachedAccount.id === account.data.id
                                    ? { ...cachedAccount, ...account.data }
                                    : cachedAccount,
                            ),
                    );
                }
            }

            if (isInSelectedAccounts) {
                store.dispatch(
                    setGeneralLoadingStatus({
                        key: DATA_LOADING_KEYS.COMBINED_LEGS,
                        status: LOADING_STATE.LOADING,
                    }),
                );

                const { data } = await getLegsForMultipleAccounts(params);
                store.dispatch(setCombinedLegs(data));
            }

            await getBalanceByAccountId({
                accountId: +apiKeyId,
            });

            const futuresPnl = await getFuturesPnlApi(apiKeyId);
            if (futuresPnl?.data) {
                const pnl = futuresPnl.data.pnl || 0;
                queryClient.setQueryData(futurePnlQueryKey(apiKeyId), pnl);
            }
        } catch (error) {
            console.error(error);
        } finally {
            batch(() => {
                store.dispatch(
                    setApiKeyLoading({
                        apiKey: apiKeyId,
                        loadingKey: 'legs',
                        status: LOADING_STATE.SUCCESS,
                    }),
                );

                store.dispatch(
                    setGeneralLoadingStatus({
                        key: DATA_LOADING_KEYS.COMBINED_LEGS,
                        status: LOADING_STATE.SUCCESS,
                    }),
                );
            });
        }
    };

    private handleMemberUpdate = (message: GeneralEvent<MemberUpdatePayload>) => {
        const { data } = message.payload;
        store.dispatch(updateProfileParts(data));
    };

    private handleUsedSlotsUpdate = (message: GeneralEvent<{ data: { usedSlots: UsedSlots } }>) => {
        store.dispatch(updateUsedSlots(message.payload.data.usedSlots));
    };

    private handleVerificationPagesDeploy = (message: GeneralEvent<any>) => {
        const pageData: any = {
            id: message.payload.data.pageId,
            deployStatus: message.payload.data.status,
            deployed: message.payload.data?.deployed || null,
            deployUpdated: message.payload.data?.deployUpdated || null,
        };

        store.dispatch(updatePageById(pageData));
    };

    private handleVerificationPageUpdate = (message: GeneralEvent<any>) => {
        store.dispatch(updatePageById(message.payload.data));
    };

    private handleNotifications = (message: GeneralEvent<{ data: Notification }>) => {
        const notification = message.payload.data;
        store.dispatch(addNotificationFromWs(notification));
        store.dispatch(toggleNotificationToast(true));
    };

    private handleLeaderboardUpdate = (message: GeneralEvent<any>) => {
        console.log('LEADERBOARD_UPDATE', message);
    };

    private handleMemberSettingsChanged = (message: GeneralEvent<any>) => {
        console.log('MEMBER_SETTING_UPDATE', message);
    };

    private handleLeaderboardSettingsChanged = (message: GeneralEvent<any>) => {
        const settings = message.payload.data?.settings?.[0]?.anonymInLeaderBoard || null;
        const state = store.getState();
        const { anonymInLeaderBoard } = state.leaderboard.settings;
        if (settings && settings !== anonymInLeaderBoard) {
            store.dispatch(updateLeaderboardSettings({ anonymInLeaderBoard: settings }));
        }
    };

    private handleActivityStreamUpdate = (message: GeneralEvent<any>) => {
        const { events = [] } = message?.payload?.data || { events: [] };

        if (events && events.length > 0) {
            syncStoreService.setActivityStreamRowsInTransaction(events);
        }
    };
}

const instance = new GeneralEventsHandler();

export default instance;
